import { useMutation, useQuery, useQueryClient } from 'react-query';
import { superAdminApi } from './api';
import type { SuperAdminFilters, SuperAdminFormValues, SuperAdminUpdateValues } from './types';

const SUPER_ADMIN_QUERY_KEY = 'super-admins';

export const useSuperAdminList = (filters: SuperAdminFilters = {}) => {
  return useQuery([SUPER_ADMIN_QUERY_KEY, filters], () => superAdminApi.list(filters), {
    staleTime: 5 * 60 * 1000, // 5 phút
    cacheTime: 10 * 60 * 1000, // 10 phút
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });
};

export const useSuperAdminSummary = (filters: SuperAdminFilters = {}) => {
  return useQuery([SUPER_ADMIN_QUERY_KEY, 'summary', filters], () => superAdminApi.summary(filters), {
    keepPreviousData: true,
  });
};

export const useCreateSuperAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation((payload: SuperAdminFormValues) => superAdminApi.create(payload), {
    onSuccess: (newRecord) => {
      // Smart cache update: add new record to all relevant queries
      queryClient.setQueriesData([SUPER_ADMIN_QUERY_KEY], (old: any) => {
        if (!old) return old;
        // Handle both array and object responses
        if (Array.isArray(old)) {
          return [newRecord, ...old];
        }
        return old;
      });
      // Invalidate to ensure consistency
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === SUPER_ADMIN_QUERY_KEY,
      });
    },
  });
};

export const useUpdateSuperAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation(({ id, payload }: { id: string; payload: SuperAdminUpdateValues }) => superAdminApi.update(id, payload), {
    onSuccess: (updated) => {
      // Smart cache update: update record in cache
      queryClient.setQueriesData([SUPER_ADMIN_QUERY_KEY], (old: any) => {
        if (!old) return old;
        if (Array.isArray(old)) {
          return old.map((r) => (r.id === updated.id ? updated : r));
        }
        return old;
      });
      // Invalidate to ensure consistency
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === SUPER_ADMIN_QUERY_KEY,
      });
    },
  });
};

export const useToggleSuperAdminStatus = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ({ id, status }: { id: string; status: 'active' | 'inactive' }) => superAdminApi.toggleStatus(id, status),
    {
      onSuccess: (updated) => {
        // Smart cache update: update record in cache
        queryClient.setQueriesData([SUPER_ADMIN_QUERY_KEY], (old: any) => {
          if (!old) return old;
          if (Array.isArray(old)) {
            return old.map((r) => (r.id === updated.id ? updated : r));
          }
          return old;
        });
        // Invalidate to ensure consistency
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === SUPER_ADMIN_QUERY_KEY,
        });
      },
    }
  );
};

export const useRemoveSuperAdmin = () => {
  const queryClient = useQueryClient();
  return useMutation((id: string) => superAdminApi.remove(id), {
    onSuccess: (_, deletedId) => {
      // Smart cache update: remove record from cache
      queryClient.setQueriesData([SUPER_ADMIN_QUERY_KEY], (old: any) => {
        if (!old) return old;
        if (Array.isArray(old)) {
          return old.filter((r) => r.id !== deletedId);
        }
        return old;
      });
      // Invalidate to ensure consistency
      queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] === SUPER_ADMIN_QUERY_KEY,
      });
    },
  });
};

export const useResetSuperAdminPassword = () => {
  return useMutation((email: string) => superAdminApi.resetPassword(email));
};

